import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SidebarProvider } from '@/components/ui/sidebar'
import { AppSidebar } from '@/components/layout/AppSidebar'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { TrendingUp, Activity, Gauge, Clock } from 'lucide-react'

// Mock data for charts (replace with real API calls)
const dailyMileage = [
  { day: 'Mon', miles: 1200 },
  { day: 'Tue', miles: 1450 },
  { day: 'Wed', miles: 1100 },
  { day: 'Thu', miles: 1680 },
  { day: 'Fri', miles: 1520 },
  { day: 'Sat', miles: 890 },
  { day: 'Sun', miles: 750 },
]

const speedDistribution = [
  { range: '0-20', count: 15 },
  { range: '20-40', count: 35 },
  { range: '40-60', count: 78 },
  { range: '60-80', count: 45 },
  { range: '80+', count: 8 },
]

const fleetStatus = [
  { name: 'Active', value: 42, color: '#22c55e' },
  { name: 'Idle', value: 18, color: '#eab308' },
  { name: 'Offline', value: 8, color: '#6b7280' },
]

const hosCompliance = [
  { day: 'Mon', compliant: 95, violations: 5 },
  { day: 'Tue', compliant: 92, violations: 8 },
  { day: 'Wed', compliant: 97, violations: 3 },
  { day: 'Thu', compliant: 94, violations: 6 },
  { day: 'Fri', compliant: 89, violations: 11 },
  { day: 'Sat', compliant: 100, violations: 0 },
  { day: 'Sun', compliant: 100, violations: 0 },
]

interface StatCard {
  title: string
  value: string
  change: string
  icon: React.ElementType
  trend: 'up' | 'down'
}

const stats: StatCard[] = [
  {
    title: 'Total Fleet Miles',
    value: '8,590',
    change: '+12.5%',
    icon: TrendingUp,
    trend: 'up',
  },
  {
    title: 'Active Vehicles',
    value: '42',
    change: '+2',
    icon: Activity,
    trend: 'up',
  },
  {
    title: 'Avg Speed',
    value: '54 mph',
    change: '-3.2%',
    icon: Gauge,
    trend: 'down',
  },
  {
    title: 'Idle Time',
    value: '2.4 hrs',
    change: '-8.1%',
    icon: Clock,
    trend: 'down',
  },
]

export function Analytics() {
  // TODO: Use real fleet stats data when implementing dynamic stat cards
  // const { data: fleetStats } = useQuery({
  //   queryKey: ['fleet-stats'],
  //   queryFn: async () => {
  //     const res = await fetch('/api/fleet/stats')
  //     if (!res.ok) throw new Error('Failed to fetch fleet stats')
  //     return res.json()
  //   },
  //   refetchInterval: 60000,
  // })

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full">
        <AppSidebar />
        <main className="flex-1 overflow-auto p-6 space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Analytics</h1>
            <p className="text-muted-foreground">
              Fleet performance metrics and insights
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => {
              const Icon = stat.icon
              return (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                      {stat.title}
                    </CardTitle>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <p className={`text-xs ${
                      stat.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change} from last week
                    </p>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Charts Row 1 */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Daily Mileage</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyMileage}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="miles"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Fleet Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={fleetStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry: any) =>
                        `${entry.name} ${(entry.percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {fleetStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Speed Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={speedDistribution}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="range" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                    <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>HOS Compliance Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={hosCompliance}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="day" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="compliant"
                      stroke="#22c55e"
                      strokeWidth={2}
                      name="Compliant %"
                    />
                    <Line
                      type="monotone"
                      dataKey="violations"
                      stroke="#ef4444"
                      strokeWidth={2}
                      name="Violations"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  )
}
